import * as React from 'react';

import { translate } from '~l10n/client';
import { debounce } from 'underscore';

import { Autosuggest } from '~shared/components/Autosuggest';
import { AutosuggestValue } from '~shared/components/Autosuggest/types';

import { searchForSchool } from '../../../../app/src/api/schoolSearch/schoolSearch';
import { buildAddressString } from './buildAddressString';

const debouncedSchoolSearch = debounce(searchForSchool, 600);

interface Props {
  legacyModel: any;
  onCreateSchoolClicked: () => void;
  onSchoolSelected?: (any) => void;
}

export function SchoolSearch(props: Props) {
  const { legacyModel, onCreateSchoolClicked, onSchoolSelected } = props;
  const [searchValue, setSearchValue] = React.useState(
    legacyModel.schoolSearchValue
  );
  const [searchResults, setSearchResults] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [showSearchError, setShowSearchError] = React.useState(false);
  const [noResultsNode, setNoResultsNode] = React.useState(null);

  const onSearchChange = (data) => {
    setSearchValue(data);
    legacyModel.schoolSearchValue = data;

    const value = data.value;
    if (value) {
      legacyModel.blingId = value.id;
      legacyModel.city = value.address_data.city;
      legacyModel.state = value.address_data.state;
      legacyModel.country = value.address_data.country;
      legacyModel.zipCode = value.address_data.postal_code;
      legacyModel.description = legacyModel.initialDescription =
        buildAddressString(value.address_data);
      legacyModel.schoolName = legacyModel.initialSchoolName = value.name;
      if (onSchoolSelected) {
        onSchoolSelected(value);
      }
    } else if (data.text.length >= 3) {
      setIsSearching(true);
      setShowSearchError(false);
      setNoResultsNode(
        <div
          style={{ padding: '1em', display: 'flex', justifyContent: 'center' }}
        >
          {translate('No results for')} {data.text}
        </div>
      );

      legacyModel.blingId =
        legacyModel.city =
        legacyModel.state =
        legacyModel.country =
        legacyModel.zipCode =
        legacyModel.description =
          null;

      const trimmedText = data.text.trim();
      if (trimmedText.length < 3) {
        setIsSearching(false);
        setSearchResults([]);
        return;
      }

      debouncedSchoolSearch({
        terms: trimmedText,
        onSuccess: (data) => {
          setIsSearching(false);
          const results: AutosuggestValue[] = data.map((item) => {
            return {
              text: item.name,
              description: buildAddressString(item.address_data),
              value: item,
            };
          });

          setSearchResults(results);
        },
        onError: (error) => {
          console.error(error);
          setIsSearching(false);
          setSearchResults([]);
        },
      });
    }
  };

  const AddSchoolNote = () => {
    return (
      <div
        className="px-6 py-4 text-base leading-6 text-blue-900 border-0 border-t border-solid border-divider"
        style={{ padding: '12px', borderTop: '1px solid #bbb' }}
      >
        {translate('Can’t find it?')}{' '}
        <button
          onMouseDown={() => {
            legacyModel.schoolName = searchValue.text
              ? searchValue.text.trim()
              : '';

            onCreateSchoolClicked();
          }}
          className="link"
          style={{
            padding: 0,
            margin: 0,
            fontSize: '14px',
            background: 'none',
            border: 0,
          }}
        >
          {translate('Add it here')}
        </button>
      </div>
    );
  };

  return (
    <Autosuggest
      id="school-search"
      value={searchValue}
      onChange={onSearchChange}
      maxLength={100}
      placeholder={translate('name, city, country')}
      aria-labelledby="school-search-label"
      containerClass={
        legacyModel.blingId == null && showSearchError
          ? 'custom-select-container-error'
          : ''
      }
      suggestions={searchResults}
      showSpinner={isSearching}
      onBlur={() => setShowSearchError(true)}
      pinnedItem={<AddSchoolNote />}
      noResultsNode={noResultsNode}
    />
  );
}
